import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { BulkImportModal } from './BulkImportModal';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { Upload, RefreshCw } from 'lucide-react';
import { useImportStore } from '@/stores/importStore';

interface BulkImportButtonProps {
  providerId: number;
  versionId: number;
  disabled?: boolean;
}

export function BulkImportButton({ 
  providerId, 
  versionId,
  disabled 
}: BulkImportButtonProps) {
  const [open, setOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const importStore = useImportStore();

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    // Reset error state when opening the modal
    if (isOpen) {
      setHasError(false);
    }
  };

  // Reset error state when import is complete
  useEffect(() => {
    if (importStore.currentSession?.status === 'completed' || 
        importStore.currentSession?.status === 'completed_with_errors') {
      setHasError(false);
    }
  }, [importStore.currentSession?.status]);

  // Custom error handler for the ErrorBoundary
  const onError = () => {
    setHasError(true);
  };

  // Only show the error UI if we've detected an actual error
  const errorFallback = hasError ? (
    <div className="text-destructive">
      <p>Error loading import</p>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => window.location.reload()}
        className="flex items-center gap-2 mt-2"
      >
        <RefreshCw className="h-3 w-3" />
        Try Again
      </Button>
    </div>
  ) : null;

  return (
    <ErrorBoundary
      fallback={errorFallback}
      onError={onError}
    >
      <div>
        <Button 
          onClick={() => setOpen(true)}
          className="flex items-center gap-2"
          disabled={disabled}
        >
          <Upload className="h-4 w-4 mr-2" />
         Bulk Import
        </Button>

        <BulkImportModal
          providerId={providerId}
          versionId={versionId}
          open={open}
          onOpenChange={handleOpenChange}
        />
      </div>
    </ErrorBoundary>
  );
}
